/**
 * Notification Dedupe Service
 * 
 * Persistent deduplication for notifications using AsyncStorage.
 * Prevents the same notification from being sent multiple times within a cooldown period.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

const STORAGE_KEY = '@tripflow_notification_dedupe_v1';

// Default cooldowns in milliseconds
export const DEFAULT_COOLDOWNS = {
  // Document expiration - 7 days per milestone
  documentExpiration: 7 * 24 * 60 * 60 * 1000,
  // Budget threshold - 7 days per threshold
  budget: 7 * 24 * 60 * 60 * 1000,
  // Landmarks - 24 hours per place
  landmark: 24 * 60 * 60 * 1000,
  // Navigation prompts - 30 minutes per item
  navigation: 30 * 60 * 1000,
  // Weather alerts - 12 hours per alert
  weather: 12 * 60 * 60 * 1000,
  // Destination news digest (one push per window, not per headline)
  safetyNewsDigest: 6 * 60 * 60 * 1000,
  // Safety zones - 24 hours per zone
  safetyZone: 24 * 60 * 60 * 1000,
  // Base camp arrival - 6 hours
  baseCamp: 6 * 60 * 60 * 1000,
  // Transport hub arrival - 6 hours
  transportHub: 6 * 60 * 60 * 1000,
  // Smart cue immediate - suppress repeated nudges for the same cue within a day
  // (scheduled reminders use their own cueKey-based dedupe).
  smartCueImmediate: 24 * 60 * 60 * 1000,
  // Day briefing - one scheduled push per trip per fire date.
  dayBriefing: 20 * 60 * 60 * 1000,
  // Geofence check-in - no cooldown (one-time per item)
  geofenceCheckIn: 0,
};

export type DedupeEntry = {
  key: string;
  sentAt: number;
  expiresAt: number;
};

type DedupeStore = {
  entries: DedupeEntry[];
  lastPruned: number;
};

// In-memory cache to avoid repeated AsyncStorage reads
let memoryCache: DedupeStore | null = null;
let cacheLoadPromise: Promise<DedupeStore> | null = null;

/**
 * Load the dedupe store from AsyncStorage
 */
const loadStore = async (): Promise<DedupeStore> => {
  // Return cached if available
  if (memoryCache) {
    return memoryCache;
  }
  
  // If already loading, wait for that promise
  if (cacheLoadPromise) {
    return cacheLoadPromise;
  }
  
  cacheLoadPromise = (async () => {
    try {
      const raw = await AsyncStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as DedupeStore;
        if (parsed && Array.isArray(parsed.entries)) {
          memoryCache = parsed;
          return parsed;
        }
      }
    } catch (error) {
      console.error('[NotificationDedupe] Error loading store:', error);
    }
    
    // Return empty store
    const emptyStore: DedupeStore = { entries: [], lastPruned: Date.now() };
    memoryCache = emptyStore;
    return emptyStore;
  })();
  
  const result = await cacheLoadPromise;
  cacheLoadPromise = null;
  return result;
};

/**
 * Save the dedupe store to AsyncStorage
 */
const saveStore = async (store: DedupeStore): Promise<void> => {
  try {
    memoryCache = store;
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (error) {
    console.error('[NotificationDedupe] Error saving store:', error);
  }
};

/**
 * Prune expired entries from the store
 */
export const pruneExpired = async (): Promise<void> => {
  const store = await loadStore();
  const now = Date.now();
  
  // Only prune if it's been at least 1 hour since last prune
  if (now - store.lastPruned < 60 * 60 * 1000) {
    return;
  }
  
  const validEntries = store.entries.filter((entry) => entry.expiresAt > now);
  
  if (validEntries.length !== store.entries.length) {
    await saveStore({
      entries: validEntries,
      lastPruned: now,
    });
  }
};

/**
 * Check if a notification with the given key is a duplicate (within cooldown)
 * 
 * @param key - Unique identifier for the notification (e.g., "budget:tripId:80")
 * @returns true if this is a duplicate that should be suppressed
 */
export const isDuplicate = async (key: string): Promise<boolean> => {
  const store = await loadStore();
  const now = Date.now();
  
  const entry = store.entries.find((e) => e.key === key);
  if (entry && entry.expiresAt > now) {
    return true;
  }
  
  return false;
};

/**
 * Mark a notification as sent with the given cooldown period
 * 
 * @param key - Unique identifier for the notification
 * @param cooldownMs - Cooldown period in milliseconds (0 means no future dedupe)
 */
export const markSent = async (key: string, cooldownMs: number): Promise<void> => {
  if (cooldownMs <= 0) {
    // No cooldown, no need to track
    return;
  }
  
  const store = await loadStore();
  const now = Date.now();
  
  // Remove any existing entry for this key
  const filteredEntries = store.entries.filter((e) => e.key !== key);
  
  // Add new entry
  filteredEntries.push({
    key,
    sentAt: now,
    expiresAt: now + cooldownMs,
  });
  
  await saveStore({
    entries: filteredEntries,
    lastPruned: store.lastPruned,
  });
  
  // Opportunistically prune expired entries
  pruneExpired().catch(() => {});
};

/**
 * Check if duplicate and mark as sent in one atomic operation
 * 
 * @param key - Unique identifier for the notification
 * @param cooldownMs - Cooldown period in milliseconds
 * @returns true if this is a duplicate that should be suppressed
 */
export const checkAndMark = async (key: string, cooldownMs: number): Promise<boolean> => {
  const duplicate = await isDuplicate(key);
  if (duplicate) {
    return true;
  }
  
  await markSent(key, cooldownMs);
  return false;
};

/**
 * Clear all dedupe entries (useful for testing or user-requested reset)
 */
export const clearAll = async (): Promise<void> => {
  memoryCache = { entries: [], lastPruned: Date.now() };
  await AsyncStorage.removeItem(STORAGE_KEY);
};

/**
 * Get the remaining cooldown time for a key (useful for debugging/UI)
 * 
 * @returns remaining milliseconds, or 0 if not in cooldown
 */
export const getRemainingCooldown = async (key: string): Promise<number> => {
  const store = await loadStore();
  const now = Date.now();
  
  const entry = store.entries.find((e) => e.key === key);
  if (entry && entry.expiresAt > now) {
    return entry.expiresAt - now;
  }
  
  return 0;
};

/**
 * Build a dedupe key for common notification types
 */
export const buildDedupeKey = {
  budget: (tripId: string, thresholdPercent: number) => 
    `budget:${tripId}:${Math.round(thresholdPercent * 100)}`,
  
  documentExpiration: (docId: string, milestone: 'expired' | '30' | '90') => 
    `doc-${milestone}:${docId}`,
  
  weather: (tripId: string, alertType: 'alert' | 'forecast', dateOrKey: string) => 
    `weather-${alertType}:${tripId}:${dateOrKey}`,
  
  landmark: (placeId: string) => 
    `landmark:${placeId}`,
  
  navigation: (tripId: string, itemId: string) => 
    `nav:${tripId}:${itemId}`,
  
  safetyZone: (zoneId: string) => 
    `safety:${zoneId}`,

  /** One notification summarizing multiple advisories (dedupe by stable id set). */
  safetyAdvisoryBatch: (sortedAdvisoryKeys: string) =>
    `safety:batch:${sortedAdvisoryKeys}`,

  /** Caps how often we ping for new destination headlines. */
  safetyNewsDigest: () => `safety:news_digest`,
  
  baseCamp: (tripId: string) => 
    `basecamp:${tripId}`,
  
  transportHub: (tripId: string, hubId: string) => 
    `hub:${tripId}:${hubId}`,
  
  geofenceCheckIn: (tripId: string, itemId: string) => 
    `checkin:${tripId}:${itemId}`,
  
  smartCue: (tripId: string, cueId: string, timing: string) => 
    `smartcue:${tripId}:${cueId}:${timing}`,

  dayBriefing: (tripId: string, triggerDateKey: string) =>
    `day-briefing:${tripId}:${triggerDateKey}`,
};
